import { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import ReportProblemButton from '@components/common/report_problem_button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import ReportProblemSubview from '../report_problem_subview';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType
} from '@xeppt/xeppt-sdk/types';
import type { TTransactionWithReferenceCode } from 'src/types/reference_code';
import { currencies } from '@const/currency';
import {
    formatDate,
    getFirstLettersBySpace,
    getLoyaltyLevelString,
    getTransactionStatus,
    prettifyAmount
} from '@utils/index';
import { enumRecipientTranslate, ETransactionStatusTranslate } from '@locales/index';
import { useTranslation } from 'react-i18next';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiAccountService, apiOrderService, apiTransactionsService, sdk } from '@api';
import useModalContext from '@hooks/context/useModalContext';
import { modalIds } from '@const/modals';
import { useLocales } from '@hooks/helpers/useLocales';
import { useUserContext } from '@hooks/context/useUserContext';
import Info from '@components/common/info';
import { formatFeeRate, getFeesPlanSendAndRequest, getInternalTransferAction } from '@utils/payment';
import { resolvePaymentSubtitleKey } from '@utils/purchase_labels';
import { EFundsOperation, ENewNotificationType } from '@enum';
import { useNotificationsContext } from '@hooks/context/useNotificationsContext';
import Typography from '@components/common/typography';
import InfoBlock from '@components/common/info_block';
import Tabs from '@components/tabs';

type AmountRowString = {
    neutral: string;
    past: string;
    amount: number;
    labelNeutral?: string;
    labelPast?: string;
};

const fullInfoStatuses = [
    ETransactionStatus.PENDING,
    ETransactionStatus.APPROVED,
    ETransactionStatus.DECLINED
];

const ViewTransactionModal: FC<
    IModalComponentProps<{
        transaction: TTransactionWithReferenceCode;
        refetchTransactions?: () => void;
        onBack?: () => void;
    }>
> = ({ onClose, modalData }) => {
    const { t, i18n } = useTranslation('transaction_details');
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();
    const { refetchAccount, loyalty } = useUserContext();
    const { triggerNotificationType } = useNotificationsContext();
    const [reminderTimer, setReminderTimer] = useState(0);
    const [isListOpen, setIsListOpen] = useState(false);
    const [showFees, setShowFees] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    // Local block state so the modal stays OPEN and the label flips in real time on
    // block/unblock — the transaction prop's is_blocked does not update in place.
    const [isBlocked, setIsBlocked] = useState(
        (modalData?.transaction as any)?.is_blocked === true
    );
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [refundTab, setRefundTab] = useState<'details' | 'refunds'>('details');
    const avatarWrapperRef = useRef<HTMLDivElement>(null);
    const { onOpen, changeModalData } = useModalContext();

    // Close the kebab dropdown when the user clicks outside the avatar wrapper.
    useEffect(() => {
        if (!isDropdownOpen) return;
        const handleOutside = (e: MouseEvent) => {
            if (avatarWrapperRef.current && !avatarWrapperRef.current.contains(e.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [isDropdownOpen]);

    // `transaction` can legitimately be absent for a render or two while this drawer is
    // opening/updating. Every hook below must still run unconditionally (Rules of Hooks),
    // so each hook guards its own body against an absent transaction; the actual
    // "nothing to show" bail-out happens once, after every hook, right before the JSX
    // return at the bottom of this component.
    const transaction = modalData?.transaction;
    const refetchTransactions = modalData?.refetchTransactions;

    // Cast the merchant-view flag — distinguishes the merchant's leg of a sale/refund
    // from the consumer's leg (SDK type does not declare is_merchant_view yet).
    const isMerchantView = (transaction as any)?.details?.is_merchant_view === true;
    const isMerchantSale = isMerchantView && transaction?.action === ETransactionAction.PAYMENT;
    const isMerchantRefund = isMerchantView && transaction?.action === ETransactionAction.REFUND;

    // Customer-side (non-merchant) purchase or refund. Used to:
    // • render a store glyph on cream bg instead of initials
    // • suppress the @handle sub-label (Figma: "Online store" only)
    // • suppress the fee breakdown row (fee is folded into "Amount you paid"); the
    //   buyer's own purchase leg (isCustomerPurchase below) is the one exception — it
    //   shows the row when an admin-configured buyer fee (1_1_6) is actually > 0
    // • suppress the Info tooltip on the Points row
    const isCustomerPurchaseOrRefund =
        !isMerchantView &&
        (transaction?.action === ETransactionAction.PAYMENT ||
            transaction?.action === ETransactionAction.REFUND);
    // True for any of the 4 purchase/refund views. Controls shared guards.
    const isPaymentRefundView = isMerchantSale || isMerchantRefund || isCustomerPurchaseOrRefund;
    // Narrower than isCustomerPurchaseOrRefund: only the buyer's PAYMENT leg (excludes
    // the customer REFUND leg and the merchant sale leg). Gates the business-category
    // subtitle, short reference codes, and points-earned row added for the
    // Pay-with-XEPPT customer purchase drawer — scope intentionally excludes the
    // other 3 purchase/refund views so they render unchanged.
    const isCustomerPurchase =
        isCustomerPurchaseOrRefund && transaction?.action === ETransactionAction.PAYMENT;

    // Cast the new backend refund fields — SDK type does not declare them yet
    // (same pattern as counterparty_avatar / cc4735ac refund fields).
    const refunds = (transaction as any)?.details?.refunds as
        Array<{ amount: string; date: string; reason?: string }> | undefined;
    const refundedAmount = (transaction as any)?.details?.refunded_amount as string | undefined;
    const remainingAmount = (transaction as any)?.details?.remaining_amount as string | undefined;

    // Cast the new customer-purchase-drawer field — SDK type does not declare it yet
    // (same any-cast pattern as the refund fields above).
    const counterpartySubtitle = (transaction as any)?.details?.counterparty_subtitle as
        | string
        | undefined;
    // Typed via the local SDK-gap extension (src/types/reference_code.ts) — no `as any`
    // cast needed. Gated follow-up: drop the extension once @xeppt/xeppt-sdk publishes
    // `details.order_reference_code`.
    const orderReferenceCode = transaction?.details.order_reference_code;
    // Gate: only PAYMENT transactions that have at least one refund entry get the new UI.
    // Exclude merchant views: the merchant PAYMENT leg must not receive the
    // consumer-facing "Refunded" pill or purchase-refunded banner.
    const hasRefunds =
        !isMerchantView &&
        transaction?.action === ETransactionAction.PAYMENT &&
        (refunds?.length ?? 0) > 0;

    const feePlan = useMemo(() => {
        if (!transaction) return undefined;
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        if (!internalAction) return undefined;
        return getFeesPlanSendAndRequest({ loyalty, method: internalAction });
    }, [transaction, loyalty]);

    const feeLabel = useMemo(() => {
        // The XEPPT fee is a single combined fee (percentage + flat), configured in
        // admin → Fees. There is no separate e-Transfer/provider fee. Show each
        // component only when it is non-zero (never "0%"/"0 CAD"); currency follows
        // the transaction (multi-currency ready) — not hardcoded.
        const rate = formatFeeRate({
            percent: feePlan?.percent,
            flat: feePlan?.flat,
            currency: transaction?.currency ?? 'CAD'
        });
        return rate ? `XEPPT fee (${rate})` : 'XEPPT fee';
    }, [feePlan, transaction]);

    const formattedFeeAmount = useMemo(() => {
        const symbol = currencies[transaction?.currency as 'CAD']?.symbol ?? '$';
        const amount = prettifyAmount(transaction?.fee?.toFixed(2) ?? '0.00');
        const currency = transaction?.currency ?? 'CAD';
        return `${symbol}${amount} ${currency}`;
    }, [transaction]);

    const tierName = useMemo(() => getLoyaltyLevelString(loyalty?.level), [loyalty]);

    const feeInfoText = useMemo(() => {
        const rateStr = formatFeeRate({
            percent: feePlan?.percent,
            flat: feePlan?.flat,
            currency: transaction?.currency ?? 'CAD'
        });
        // Refund fees are included in the total charged to the wallet (amount + fee),
        // not "the amount you send", and there is no cancel-the-transfer clause.
        if (transaction?.action === ETransactionAction.REFUND) {
            const who = tierName ? `Fees for ${tierName}-level users are` : 'Fees are';
            return rateStr
                ? `${who} ${rateStr}, included in the total charged for this refund.`
                : 'Fees are included in the total charged for this refund.';
        }
        if (rateStr) {
            const who = tierName ? `Fees for ${tierName}-level users are` : 'Fees are';
            return `${who} ${rateStr}, included in the amount you send. This fee is charged if you cancel the transfer.`;
        }
        return 'Fees are included in the amount you send. This fee is charged if you cancel the transfer.';
    }, [feePlan, tierName, transaction]);

    const isTransactionRequest = useMemo(
        () =>
            transaction?.action === ETransactionAction.REQUEST &&
            transaction?.status === ETransactionStatus.PENDING,
        [transaction]
    );

    const isCreditRequest = useMemo(() => {
        if (!transaction) return false;
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        return internalAction === EFundsOperation.WALLET_CREDIT_REQUEST;
    }, [transaction]);

    const isDebitRequest = useMemo(() => {
        if (!transaction) return false;
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        return internalAction === EFundsOperation.WALLET_DEBIT_REQUEST;
    }, [transaction]);

    const isInternalTransactionRequest = useMemo(
        () => isTransactionRequest && transaction?.method === ETransactionMethod.WALLET,
        [transaction]
    );

    // External (non-member) WALLET recipients have no details.user_id set by the backend.
    // Gate to TRANSFER/REQUEST only — CARD_DEPOSIT and CARD_SERVICE are also method=WALLET
    // but have a user_id-less details object that is unrelated to recipient display.
    // A registered member is identified by an "@handle" in source/destination.
    // details.user_id alone is unreliable: scheduled (automatic) member-to-member
    // transfers arrive with an empty details object, so a member would be wrongly
    // labelled "External contact". Gate on the handle too — only treat as external
    // when there is neither a user_id nor a member handle.
    const isExternalWallet = useMemo(() => {
        if (!transaction) return false;
        const counterparty =
            transaction.type === ETransactionType.CREDIT
                ? transaction.destination
                : transaction.source;
        const hasMemberHandle = (counterparty || '').includes('(@');
        return (
            transaction.method === ETransactionMethod.WALLET &&
            !transaction.details?.user_id &&
            !hasMemberHandle &&
            (transaction.action === ETransactionAction.TRANSFER ||
                transaction.action === ETransactionAction.REQUEST)
        );
    }, [transaction]);

    // Non-null for WALLET PAYMENT transactions; value is the subtitle locale key for the
    // counterparty block (buyer sees 'online_store', merchant sees 'xeppt_user').
    const paymentSubtitleKey = useMemo(() => {
        if (!transaction) return undefined;
        return resolvePaymentSubtitleKey(transaction.action, transaction.type);
    }, [transaction]);

    const isNotMessage = useMemo(() => {
        if (transaction) {
            // PAYMENT removed: payment-popup purchases may carry a message from the buyer
            const actions = [ETransactionAction.CARD_SERVICE, ETransactionAction.CARD_DEPOSIT];
            const methods = [ETransactionMethod.EFT];
            return actions.includes(transaction?.action) || methods.includes(transaction.method);
        } else return false;
    }, [transaction]);

    const getRecipientName = useMemo(() => {
        if (!transaction) return '';
        // A refund leg's `source` always holds the counterparty regardless of
        // type: the merchant's own refund leg is CreditType with the CUSTOMER
        // in `source` (destination is the merchant's own wallet), and the
        // customer's refund leg is DebitType with the STORE in `source` —
        // already correct via the type ternary below, so this is a no-op for
        // that case. Mirrors transaction_item.tsx's getTransactionTitle
        // "refund → source" branch.
        if (transaction.action === ETransactionAction.REFUND) {
            return transaction.source.split('(')?.[0] || '';
        }
        if (transaction.type === ETransactionType.CREDIT) {
            return transaction.destination.split('(')?.[0] || '';
        } else {
            return transaction.source.split('(')?.[0] || '';
        }
    }, [transaction]);

    const getAvatar = useMemo(() => {
        if (!transaction) return '';
        // Customer-side purchase / refund: prefer the merchant's uploaded store logo;
        // fall back to the store glyph on cream bg (#FDF1DC) when no logo is set.
        // The background is applied via .store_avatar on the wrapper div.
        if (isCustomerPurchaseOrRefund) {
            const merchantLogo = (transaction as any).details?.counterparty_avatar;
            if (merchantLogo && typeof merchantLogo === 'string') {
                return <img src={merchantLogo} alt={getRecipientName} />;
            }
            return (
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}>
                    <path
                        d="M21 9H3M21 9V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V9M21 9L18 5H6L3 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M10 21V15H14V21"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <rect
                        x="5.5"
                        y="12"
                        width="3"
                        height="3"
                        rx="0.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    />
                    <rect
                        x="15.5"
                        y="12"
                        width="3"
                        height="3"
                        rx="0.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    />
                </svg>
            );
        }
        // Also covers the MERCHANT's own view of a sale/refund (isMerchantSale /
        // isMerchantRefund): the backend sets this same field to the CUSTOMER's
        // avatar on the merchant's leg, and isCustomerPurchaseOrRefund is false
        // for that view, so execution reaches here and shows the photo —
        // falling to initials below when no photo is set. Mirrors
        // transaction_item.tsx's customerAvatar for the list-row equivalent.
        const counterpartyAvatar = (transaction as any).details?.counterparty_avatar;
        if (counterpartyAvatar && typeof counterpartyAvatar === 'string') {
            return <img src={counterpartyAvatar} alt={getRecipientName} />;
        }
        if (
            transaction.method === ETransactionMethod.WALLET ||
            transaction.method === ETransactionMethod.E_TRANSFER
        ) {
            if (transaction.action === ETransactionAction.CARD_DEPOSIT) {
                return <Icon name="credit_card" width={24} height={24} />;
            }
            return getFirstLettersBySpace(getRecipientName?.split(' ')?.slice(0, 2).join(' '));
        } else if (ETransactionMethod.EFT === transaction.method) {
            return <Icon name="bank" width={24} height={24} />;
        } else if (ETransactionMethod.EPS === transaction.method) {
            return <Icon name="credit_card" width={24} height={24} />;
        } else if (ETransactionMethod.PAY_BILL === transaction.method) {
            return <Icon name="pay_bill" width={24} height={24} />;
        } else if (ETransactionMethod.CARD === transaction.method) {
            return <Icon name="credit_card" width={24} height={24} />;
        } else if (transaction.method === ETransactionMethod.GIFT_CARD) {
            return <Icon name="gift_card" width={24} height={24} />;
        }
        return '';
    }, [transaction, getRecipientName, isCustomerPurchaseOrRefund]);

    const getEmail = useMemo(() => {
        if (!transaction) return '';
        // Same refund exception as getRecipientName above — keep the handle
        // in sync with the name shown.
        if (transaction.action === ETransactionAction.REFUND) {
            return transaction.source.split('(')?.[1]?.replace(')', '') || '';
        }
        if (transaction.type === ETransactionType.CREDIT) {
            return transaction.destination.split('(')?.[1]?.replace(')', '') || '';
        } else {
            return transaction.source.split('(')?.[1]?.replace(')', '') || '';
        }
    }, [transaction]);

    const { handleRequest: handleApproveTransaction, isLoading: isApproveLoading } = useApiMutation(
        {
            method: () =>
                apiTransactionsService.approveRequestMoneyWallet(transaction?.id as string),
            onSuccess: () => {
                if (refetchTransactions) {
                    refetchTransactions();
                } else {
                    triggerNotificationType(ENewNotificationType.TRANSACTION);
                }
                requestSuccessLocale('approve_transaction');
                refetchAccount();
                onClose();
            },
            onError: requestErrorLocale
        }
    );

    const { handleRequest: handleDeclineTransaction, isLoading: isDeclineLoading } = useApiMutation(
        {
            method: () =>
                apiTransactionsService.declineRequestMoneyWallet(transaction?.id as string, ''),
            onSuccess: () => {
                if (refetchTransactions) {
                    refetchTransactions();
                } else {
                    triggerNotificationType(ENewNotificationType.TRANSACTION);
                }
                requestSuccessLocale('cancel_transaction');
                refetchAccount();
                onClose();
            },
            onError: requestErrorLocale
        }
    );

    const { handleRequest: handleCancelTransaction, isLoading: isCancelLoading } = useApiMutation({
        method: () => apiTransactionsService.cancelRequestMoneyWallet(transaction?.id as string),
        onSuccess: () => {
            if (refetchTransactions) {
                refetchTransactions();
            } else {
                triggerNotificationType(ENewNotificationType.TRANSACTION);
            }
            requestSuccessLocale('cancel_transaction');
            refetchAccount();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleResendNotification, isLoading: isResendLoading } = useApiMutation({
        method: () =>
            apiTransactionsService.resendRequestMoneyNotification(transaction?.id as string),
        onSuccess: () => {
            requestSuccessLocale('resend_transaction');
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleBlockContact } = useApiMutation({
        method: (val: string) => {
            return apiAccountService.blockMoneyRequester(val);
        },
        onSuccess: () => {
            requestSuccessLocale('block_contact');
            // Keep the modal open; flip the label to "Unblock user" in place.
            setIsBlocked(true);
            if (refetchTransactions) {
                refetchTransactions();
            }
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleUnblockContact } = useApiMutation({
        method: (val: string) => {
            return sdk.request<void>({
                method: 'PATCH',
                url: '/account/unblock-request-money-wallet-requester',
                body: { id: val }
            });
        },
        onSuccess: () => {
            requestSuccessLocale('unblock_contact');
            // Keep the modal open; flip the label to "Block user" in place.
            setIsBlocked(false);
            if (refetchTransactions) {
                refetchTransactions();
            }
        },
        onError: requestErrorLocale
    });

    // Re-open the current transaction when the user clicks the back arrow after
    // navigating to a related transaction. Captures the current transaction + refetch
    // callback so the closure is always pointing at the right data for this render.
    const reopenCurrentTxn = () => {
        changeModalData({ transaction, refetchTransactions });
        onOpen({ modalId: modalIds.VIEW_TRANSACTION });
    };

    const { handleRequest: fetchLinkedTxn, isLoading: isLinkedTxnLoading } = useApiMutation({
        method: (id: string) => apiTransactionsService.getTransactionById(id),
        onSuccess: (fetched) => {
            changeModalData({ transaction: fetched, onBack: reopenCurrentTxn });
            onOpen({ modalId: modalIds.VIEW_TRANSACTION });
        },
        onError: requestErrorLocale
    });

    const { handleRequest: fetchLinkedOrder, isLoading: isLinkedOrderLoading } = useApiMutation({
        method: (id: string) => apiOrderService.getOrderById(id),
        onSuccess: (fetched) => {
            changeModalData({ order: fetched });
            onOpen({ modalId: modalIds.VIEW_ORDER });
        },
        onError: requestErrorLocale
    });

    const handleSendReminder = () => {
        handleResendNotification(undefined);
        setReminderTimer(60);
        const interval = setInterval(() => {
            setReminderTimer((state) => {
                if (state === 0) {
                    clearInterval(interval);
                    return 0;
                } else {
                    return state - 1;
                }
            });
        }, 1000);
    };

    const getTransactionName = useMemo(() => {
        if (!transaction) return undefined;
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        switch (internalAction) {
            case EFundsOperation.CARD_DEBIT_DEPOSIT:
                return 'Load balance';
            case EFundsOperation.CARD_CREDIT_WITHDRAWAL:
                return 'Payment with card';
            case EFundsOperation.E_TRANSFER_DEBIT_TRANSFER:
                return 'Receive money via e-Transfer';
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return 'Send money via e-Transfer';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'Pay bill';
            case EFundsOperation.WALLET_CREDIT_CARD_SERVICE:
                return 'Order credit card';
            case EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT:
                return 'Load XEPPT card balance';
            case EFundsOperation.WALLET_CREDIT_REQUEST:
                return 'Receive request via XEPPT';
            case EFundsOperation.WALLET_DEBIT_TRANSFER:
                return 'Receive money via XEPPT';
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return 'Send money via XEPPT';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'Send request via XEPPT';
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'Send request via e-Transfer';
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return 'Withdraw to bank account';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'Load balance';
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return 'Load balance';
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return (transaction?.details?.gift_card_transaction_fee?.length || 0) > 1
                    ? 'Bulk XEPPT gift card issuance'
                    : 'XEPPT gift card issuance';
            case EFundsOperation.GIFT_CARD_CREDIT_RESERVE:
                return (transaction?.details?.gift_card_transaction_fee?.length || 0) > 1
                    ? 'Bulk XEPPT gift card funds reservation'
                    : 'XEPPT gift card funds reservation';
            case EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM:
                return 'XEPPT gift card redemption';
            case EFundsOperation.GIFT_CARD_DEBIT_RESERVE:
                return 'Return reserved funds';
            case EFundsOperation.WALLET_CREDIT_PAYMENT:
            case EFundsOperation.WALLET_DEBIT_PAYMENT:
                return t('purchase');
        }
    }, [transaction]);

    const getSenderString = useMemo(() => {
        if (!transaction) return undefined;
        if (transaction.action === ETransactionAction.REFUND) {
            return {
                neutral: 'you refund',
                past: 'you refunded',
                amount: transaction?.amount
            };
        }
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        switch (internalAction) {
            case EFundsOperation.CARD_DEBIT_DEPOSIT:
                return {
                    neutral: '',
                    past: '',
                    amount: transaction?.amount
                };
            case EFundsOperation.CARD_CREDIT_WITHDRAWAL:
                return {
                    neutral: '',
                    past: '',
                    amount: transaction?.amount + transaction?.fee
                };
            case EFundsOperation.E_TRANSFER_DEBIT_TRANSFER:
                return {
                    neutral: 'you receive',
                    past: 'you received',
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_CREDIT_CARD_SERVICE:
                return {
                    neutral: 'you charge',
                    past: 'you charged',
                    amount: transaction?.amount + transaction?.fee
                };
            case EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT:
                return {
                    neutral: t('amount_you_paid'),
                    past: t('amount_you_paid'),
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.WALLET_CREDIT_REQUEST:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.WALLET_DEBIT_TRANSFER:
                return {
                    neutral: `you receive`,
                    past: `you received`,
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return {
                    // "Amount you requested" = the GROSS charged to the payer (net + fee), matching
                    // the keyed "Amount charged to payer". The net the requester receives is shown
                    // separately by getRecipientString ("Amount you receive").
                    neutral: `you requested`,
                    past: `you requested`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return {
                    // Same as wallet request: show the GROSS charged to the payer here; the net
                    // received is shown by getRecipientString below.
                    neutral: `you requested`,
                    past: `you requested`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return {
                    neutral: `you withdrew`,
                    past: `you withdrew`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return {
                    neutral: `you request`,
                    past: `you requested`,
                    amount: transaction?.amount
                };
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return {
                    neutral: `you request`,
                    past: `you requested`,
                    amount: transaction?.amount
                };
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return {
                    neutral: `you pay`,
                    past: `you paid`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return {
                    neutral: `you pay`,
                    past: `you paid`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM:
                return {
                    neutral: `redeemed`,
                    past: `redeemed`,
                    amount: transaction?.amount
                };
            case EFundsOperation.GIFT_CARD_CREDIT_RESERVE:
                return {
                    neutral: `you paid`,
                    past: `you paid`,
                    amount: transaction?.amount + transaction?.fee
                };

            case EFundsOperation.GIFT_CARD_DEBIT_RESERVE:
                return {
                    neutral: `release`,
                    past: `released`,
                    amount: transaction?.amount
                };
            // Buyer leg (CREDIT = money out): full amount including fee.
            case EFundsOperation.WALLET_CREDIT_PAYMENT:
                return {
                    neutral: t('amount_you_paid'),
                    past: t('amount_you_paid'),
                    amount: transaction?.amount + transaction.fee
                };
            // Merchant leg (DEBIT = money in): amount only, no fee deducted.
            case EFundsOperation.WALLET_DEBIT_PAYMENT:
                return {
                    neutral: t('amount_you_received'),
                    past: t('amount_you_received'),
                    amount: transaction?.amount
                };
            default:
                return undefined;
        }
    }, [transaction, getRecipientName]);

    const getRecipientString = useMemo((): AmountRowString | undefined => {
        if (!transaction) return undefined;
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        switch (internalAction) {
            case EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT:
                return {
                    neutral: `loaded on card`,
                    past: `loaded on card`,
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_CREDIT_REQUEST:
                // Payer sees the net amount the requester asked for. Use "requested" in both
                // tenses — this is a fact about what was requested, not an in-progress action.
                return {
                    neutral: `${getRecipientName} requested`,
                    past: `${getRecipientName} requested`,
                    amount: transaction?.amount
                };
            // WALLET_DEBIT_REQUEST (requester view): only the gross "Amount you requested" row
            // is shown (getSenderString). The net breakdown is omitted per design — Figma shows
            // no second amount row for this variant.
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return {
                    neutral: `${getRecipientName} receive`,
                    past: `${getRecipientName} received`,
                    amount: transaction?.amount
                };
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return {
                    neutral: `deposited`,
                    past: `deposited`,
                    amount: transaction?.amount
                };
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return {
                    neutral: t('amount_you_received'),
                    past: t('amount_you_received'),
                    amount: transaction?.amount
                };
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return {
                    neutral: t('amount_you_received'),
                    past: t('amount_you_received'),
                    amount: transaction?.amount
                };
            // case EFundsOperation.EFT_DEBIT_DEPOSIT:
            //     return {
            //         neutral: `${getRecipientName} send`,
            //         past: `${getRecipientName} sent`,
            //         amount: transaction?.amount + transaction.fee
            //     };
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                // Figma shows "{name} got" (no "Amount" prefix) for the recipient row on
                // outgoing e-Transfer. labelNeutral/labelPast override the "Amount …" prefix.
                return {
                    neutral: `${getRecipientName} receive`,
                    past: `${getRecipientName} received`,
                    labelNeutral: `${getRecipientName} gets`,
                    labelPast: `${getRecipientName} got`,
                    amount: transaction?.amount
                };
            // E_TRANSFER_DEBIT_REQUEST (requester view): only the gross "Amount you requested"
            // row is shown (getSenderString). No net breakdown row per Figma design.
            // case EFundsOperation.EPS_DEBIT_DEPOSIT:
            //     return {
            //         neutral: `${getRecipientName} send`,
            //         past: `${getRecipientName} sent`,
            //         amount: transaction?.amount + transaction.fee
            //     };
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return {
                    neutral: `${getRecipientName} receive`,
                    past: `${getRecipientName} received`,
                    amount: transaction?.amount
                };
            case EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM:
                return {
                    neutral: `you received`,
                    past: `you received`,
                    amount: transaction?.amount
                };
            default:
                return undefined;
        }
    }, [transaction, getRecipientName]);

    const canBeRefunded = useMemo(() => {
        if (!transaction) return false;
        const methods = [ETransactionMethod.WALLET];
        const actions = [ETransactionAction.TRANSFER, ETransactionAction.REQUEST];
        return (
            transaction?.status === ETransactionStatus.APPROVED &&
            methods.includes(transaction?.method) &&
            actions.includes(transaction?.action) &&
            transaction?.type === ETransactionType.CREDIT
        );
    }, [transaction]);

    // Show the 3-dot kebab on WALLET TRANSFER/REQUEST (block/unblock scope) and
    // on merchant purchase/refund views (Figma: customer photo + kebab overlay).
    const showKebab = useMemo(
        () =>
            transaction?.status !== ETransactionStatus.ERROR &&
            ((transaction?.method === ETransactionMethod.WALLET &&
                (transaction?.action === ETransactionAction.TRANSFER ||
                    transaction?.action === ETransactionAction.REQUEST)) ||
                isMerchantSale ||
                isMerchantRefund),
        [transaction, isMerchantSale, isMerchantRefund]
    );

    // All hooks above run unconditionally on every render (Rules of Hooks). Only now,
    // after every hook has executed, do we bail out for a genuinely absent transaction.
    if (!transaction) return null;

    return (
        <div className={styles.wrapper}>
            {showReport ? (
                <ReportProblemSubview
                    transactionRef={transaction.reference_code ?? ''}
                    onBack={() => setShowReport(false)}
                    onClose={onClose}
                />
            ) : showRefund ? (
                <ReportProblemSubview
                    transactionRef={transaction.reference_code ?? ''}
                    isRefund
                    title="Request a refund"
                    onBack={() => setShowRefund(false)}
                    onClose={onClose}
                />
            ) : showFees ? (
                <div className={styles.fees_subview}>
                    <div className={styles.fees_subview_header}>
                        <button
                            type="button"
                            className={styles.fees_back_btn}
                            onClick={() => setShowFees(false)}>
                            <Icon name="arrow_left" width={20} height={20} />
                        </button>
                        <p className={styles.fees_subview_title}>Total fees</p>
                        <button
                            type="button"
                            className={styles.fees_close_btn}
                            onClick={onClose}>
                            <Icon name="close" width={20} height={20} />
                        </button>
                    </div>
                    <div className={styles.fees_rows}>
                        <div className={styles.fees_item}>
                            <p>{feeLabel}</p>
                            <p className={styles.row_value}>{formattedFeeAmount}</p>
                        </div>
                        <hr className={styles.fees_divider} />
                        <div className={styles.fees_item}>
                            <p>
                                <b>Total</b>
                            </p>
                            <p className={styles.row_value}>
                                <b>{formattedFeeAmount}</b>
                            </p>
                        </div>
                        <p className={styles.fees_subtext}>
                            {transaction?.action === ETransactionAction.REFUND
                                ? 'Included in the total charged'
                                : 'Included into the amount you sent'}
                        </p>
                    </div>
                    <InfoBlock variant="gray">{feeInfoText}</InfoBlock>
                </div>
            ) : (
            <>
            <div
                className={`${styles.txn_detail_header}${
                    modalData?.onBack ? ` ${styles.has_back}` : ''
                }`}>
                {modalData?.onBack && (
                    <button
                        type="button"
                        className={styles.txn_detail_back_btn}
                        onClick={modalData?.onBack}>
                        <Icon name="arrow_left" width={24} height={24} />
                    </button>
                )}
                <Typography fz={24} fw={700} alignment={modalData?.onBack ? 'center' : 'left'}>
                    {transaction.action === ETransactionAction.REQUEST
                        ? 'Request details'
                        : 'Transaction details'}
                </Typography>
                <button type="button" className={styles.txn_detail_close_btn} onClick={onClose}>
                    <Icon name="close" width={24} height={24} />
                </button>
            </div>
            <div className={styles.content}>
                <div className={styles.user}>
                    {/* Avatar wrapped in a relative container so the kebab dropdown can
                        be positioned relative to the avatar centre without affecting layout. */}
                    <div className={styles.avatar_wrapper} ref={avatarWrapperRef}>
                        <div
                            className={`${styles.user_avatar}${isCustomerPurchaseOrRefund ? ` ${styles.store_avatar}` : ''}`}>
                            {getAvatar}
                            {showKebab && (
                                <button
                                    type="button"
                                    className={styles.kebab}
                                    aria-label="More options"
                                    onClick={() => setIsDropdownOpen((v) => !v)}>
                                    <Icon name="more_vertical" width={14} height={14} />
                                </button>
                            )}
                        </div>
                        {showKebab && isDropdownOpen && (
                            <div className={styles.kebab_dropdown}>
                                <button
                                    type="button"
                                    className={styles.kebab_dropdown_item}
                                    onClick={() => {
                                        setIsDropdownOpen(false);
                                        if (isBlocked) {
                                            handleUnblockContact(transaction.id);
                                        } else {
                                            handleBlockContact(transaction.id);
                                        }
                                    }}>
                                    <Icon name="block" width={16} height={16} />
                                    {isBlocked ? 'Unblock user' : 'Block user'}
                                </button>
                            </div>
                        )}
                    </div>
                    <p className={styles.user_name}>
                        {isExternalWallet && !getEmail ? '' : getRecipientName}
                    </p>
                    {/* Suppress @handle for customer purchase/refund — Figma shows "Online store" only */}
                    {!isCustomerPurchaseOrRefund && (
                        <p className={styles.user_email}>
                            {isExternalWallet && !getEmail ? getRecipientName : getEmail}
                        </p>
                    )}
                    {transaction?.method !== ETransactionMethod.PAY_BILL && (
                        <p className={styles.method}>
                            {transaction?.method &&
                                // For PAYMENT legs show a purchase-specific subtitle instead of
                                // the generic "XEPPT contact" / "External contact" label.
                                // Backend/SDK gap: if getRecipientName is empty for a PAYMENT leg,
                                // the counterparty name is not yet provided in source/destination
                                // fields — a separate backend fast-follow is required.
                                (paymentSubtitleKey
                                    ? isCustomerPurchase
                                        ? counterpartySubtitle || t(paymentSubtitleKey)
                                        : t(paymentSubtitleKey)
                                    : transaction.action === ETransactionAction.REFUND
                                    ? (isMerchantRefund ? t('xeppt_user') : t('online_store'))
                                    : isExternalWallet
                                    ? i18n.language === 'fr'
                                        ? 'Contact externe'
                                        : 'External contact'
                                    : transaction.action === ETransactionAction.CARD_DEPOSIT
                                    ? enumRecipientTranslate[ETransactionMethod.CARD][
                                          i18n.language as 'en' | 'fr'
                                      ]
                                    : enumRecipientTranslate[transaction.method][
                                          i18n.language as 'en' | 'fr'
                                      ])}
                        </p>
                    )}
                    <div className={styles.divider}>
                        <Typography variant="body4">
                            <Icon
                                name={
                                    // For TRANSFER: CREDIT = money out (up), DEBIT = money in (down).
                                    // For REQUEST: semantics invert — CREDIT = payer (money will leave,
                                    // shown as down), DEBIT = requester (money will arrive, shown as up).
                                    transaction?.action === ETransactionAction.REQUEST
                                        ? transaction?.type === ETransactionType.CREDIT
                                            ? 'arrow_down'
                                            : 'arrow_up'
                                        : transaction?.type === ETransactionType.CREDIT
                                        ? 'arrow_up'
                                        : 'arrow_down'
                                }
                            />
                            {transaction.action === ETransactionAction.REFUND
                                ? 'Refund'
                                : getTransactionName}
                        </Typography>
                    </div>
                    {getSenderString && (
                        <p className={styles.card_amount}>
                            {transaction.action !== ETransactionAction.REQUEST &&
                            transaction.type === ETransactionType.CREDIT
                                ? '−'
                                : ''}
                            {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                            {prettifyAmount(
                                (isMerchantRefund
                                    ? transaction.amount + transaction.fee
                                    : getSenderString?.amount ?? 0
                                ).toFixed(2)
                            )}{' '}
                            {transaction?.currency || 'CAD'}
                        </p>
                    )}
                    {hasRefunds ? (
                        <span className={styles.refunded_status_pill}>Refunded</span>
                    ) : (
                        <Status variant={getTransactionStatus(transaction?.status)}>
                            {transaction?.status &&
                                ETransactionStatusTranslate[transaction.status][
                                    i18n.language as 'en' | 'fr'
                                ]}
                        </Status>
                    )}
                </div>
                {!isMerchantRefund &&
                    transaction.action === ETransactionAction.REFUND &&
                    (transaction as any).details?.original_purchase_amount && (
                    <InfoBlock variant="gray" className={styles.refund_info_banner}>
                        You paid {getRecipientName} $
                        {(transaction as any).details.original_purchase_amount}{' '}
                        {transaction?.currency ?? 'CAD'} on{' '}
                        {(transaction as any).details?.original_purchase_date
                            ? formatDate(
                                  (transaction as any).details.original_purchase_date,
                                  'DD MMMM YYYY'
                              )
                            : '—'}
                        .
                        {(transaction as any).details?.related_transaction_id && (
                            <>
                                <br />
                                <button
                                    type="button"
                                    className={styles.banner_link}
                                    disabled={isLinkedTxnLoading}
                                    onClick={() =>
                                        fetchLinkedTxn(
                                            (transaction as any).details.related_transaction_id
                                        )
                                    }>
                                    View purchase
                                </button>
                            </>
                        )}
                    </InfoBlock>
                )}
                {isMerchantSale && transaction.details?.order_id && (
                    <InfoBlock variant="gray" className={styles.refund_info_banner}>
                        This transaction was made for order {orderReferenceCode ?? '—'}.{' '}
                        <button
                            type="button"
                            className={styles.banner_link}
                            disabled={isLinkedOrderLoading}
                            onClick={() =>
                                fetchLinkedOrder(transaction.details?.order_id as string)
                            }>
                            View order
                        </button>
                    </InfoBlock>
                )}
                {isMerchantRefund && (
                    <InfoBlock variant="gray" className={styles.refund_info_banner}>
                        This refund was issued for the order on{' '}
                        {(transaction as any).details?.original_purchase_date
                            ? formatDate(
                                  (transaction as any).details.original_purchase_date,
                                  'DD MMMM YYYY'
                              )
                            : '—'}.
                        {transaction.details?.order_id && (
                            <>
                                <br />
                                <button
                                    type="button"
                                    className={styles.banner_link}
                                    disabled={isLinkedOrderLoading}
                                    onClick={() =>
                                        fetchLinkedOrder(transaction.details?.order_id as string)
                                    }>
                                    View order
                                </button>
                            </>
                        )}
                    </InfoBlock>
                )}
                {hasRefunds && (
                    <InfoBlock variant="gray" className={styles.purchase_refund_banner}>
                        <p className={styles.purchase_refund_text}>
                            {(refunds?.length ?? 0) === 1
                                ? <>
                                    This purchase was refunded on{' '}
                                    {formatDate(refunds![0].date, 'DD MMMM YYYY')}.{' '}
                                    {(refunds![0] as any).transaction_id && (
                                        <button
                                            type="button"
                                            className={styles.banner_link}
                                            disabled={isLinkedTxnLoading}
                                            onClick={() =>
                                                fetchLinkedTxn(
                                                    (refunds![0] as any).transaction_id
                                                )
                                            }>
                                            View refund
                                        </button>
                                    )}
                                  </>
                                : 'This purchase has been refunded multiple times.'}
                        </p>
                        <div className={styles.purchase_refund_table}>
                            <div className={styles.purchase_refund_row}>
                                <span>Original amount:</span>
                                <span>
                                    {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                    {transaction.amount?.toFixed(2)}{' '}
                                    {transaction?.currency ?? 'CAD'}
                                </span>
                            </div>
                            <div className={styles.purchase_refund_row}>
                                <span>Refunded:</span>
                                <span>
                                    {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                    {refundedAmount}{' '}
                                    {transaction?.currency ?? 'CAD'}
                                </span>
                            </div>
                            <div className={styles.purchase_refund_row}>
                                <span>Remaining amount:</span>
                                <span>
                                    {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                    {remainingAmount}{' '}
                                    {transaction?.currency ?? 'CAD'}
                                </span>
                            </div>
                        </div>
                    </InfoBlock>
                )}
                {hasRefunds && (refunds?.length ?? 0) > 1 && (
                    <Tabs
                        tabs={[
                            { label: 'Details', value: 'details' },
                            {
                                label: 'Refunds',
                                value: 'refunds'
                            }
                        ]}
                        activeTab={refundTab}
                        onChange={(val) => setRefundTab(val as 'details' | 'refunds')}
                        className={styles.purchase_refund_tabs}
                    />
                )}
                {hasRefunds && (refunds?.length ?? 0) > 1 && refundTab === 'refunds' ? (
                    <div className={styles.refund_rows}>
                        {refunds!.map((r, i) => {
                            const rowTxnId = (r as any).transaction_id as string | undefined;
                            return (
                                <div
                                    key={i}
                                    className={styles.refund_row_item}
                                    onClick={rowTxnId ? () => fetchLinkedTxn(rowTxnId) : undefined}
                                    style={rowTxnId ? { cursor: 'pointer' } : undefined}>
                                    <span className={styles.refund_row_date}>
                                        {formatDate(r.date, 'D MMM YYYY')}
                                    </span>
                                    <span className={styles.refund_row_amount}>
                                        +{currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                        {r.amount}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                <div className={styles.details}>
                    <p className={styles.details_title}>Details</p>
                    <div className={styles.row}>
                        <p>Date and time</p>
                        <p className={styles.row_value}>{formatDate(transaction.created_at, 'DD MMMM YYYY, HH:mm')}</p>
                    </div>
                    {isCreditRequest ? (
                        // WALLET_CREDIT_REQUEST (payer view): Figma order is
                        // "what was requested" → fees → "what you send (total)".
                        <>
                            {fullInfoStatuses.includes(transaction?.status) &&
                                getRecipientString && (
                                    <div className={styles.row}>
                                        <p>
                                            Amount{' '}
                                            {transaction.status !== ETransactionStatus.APPROVED
                                                ? getRecipientString?.neutral
                                                : getRecipientString?.past}
                                        </p>
                                        <p className={styles.row_value}>
                                            <b>
                                                {' '}
                                                {currencies[transaction?.currency as 'CAD']
                                                    ?.symbol || '$'}
                                                {prettifyAmount(
                                                    getRecipientString?.amount?.toFixed(2) ||
                                                        '0.00'
                                                )}{' '}
                                                {transaction?.currency || 'CAD'}
                                            </b>
                                        </p>
                                    </div>
                                )}
                            {fullInfoStatuses.includes(transaction?.status) &&
                                transaction?.fee !== 0 && (
                                    <div className={styles.row}>
                                        <p>
                                            Total fees (included)
                                            <button
                                                type="button"
                                                className={styles.fee_info_btn}
                                                onClick={() => setShowFees(true)}>
                                                <Icon name="info" width={16} height={16} />
                                            </button>
                                        </p>
                                        <p className={styles.row_value}>{formattedFeeAmount}</p>
                                    </div>
                                )}
                            {getSenderString && (
                                <div className={styles.row}>
                                    <p>
                                        Amount{' '}
                                        {transaction.status !== ETransactionStatus.APPROVED
                                            ? getSenderString?.neutral
                                            : getSenderString?.past}
                                    </p>
                                    <p className={styles.row_value}>
                                        <b>
                                            {currencies[transaction?.currency as 'CAD']?.symbol ||
                                                '$'}
                                            {prettifyAmount(
                                                getSenderString?.amount?.toFixed(2) || '0.00'
                                            )}{' '}
                                            {transaction?.currency || 'CAD'}
                                        </b>
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (
                        // All other variants: sender amount → fees → recipient amount.
                        <>
                            {getSenderString && (
                                <div className={styles.row}>
                                    <p>
                                        {isMerchantRefund
                                            ? 'Refund amount'
                                            : transaction.action === ETransactionAction.REFUND
                                            ? 'Amount you received'
                                            : `Amount ${
                                                  transaction.status !==
                                                  ETransactionStatus.APPROVED
                                                      ? getSenderString?.neutral
                                                      : getSenderString?.past
                                              }`}
                                    </p>
                                    <p className={styles.row_value}>
                                        <b>
                                            {currencies[transaction?.currency as 'CAD']?.symbol ||
                                                '$'}
                                            {prettifyAmount(
                                                getSenderString?.amount?.toFixed(2) || '0.00'
                                            )}{' '}
                                            {transaction?.currency || 'CAD'}
                                        </b>
                                    </p>
                                </div>
                            )}
                            {/* Fee breakdown: merchant sale, merchant refund, and the buyer's own
                                purchase leg all render this row when a fee was actually charged
                                (fee !== 0). The customer refund leg still folds the fee into the
                                displayed amount, so it's excluded. */}
                            {fullInfoStatuses.includes(transaction?.status) &&
                                transaction?.fee !== 0 &&
                                (isMerchantSale ||
                                    isMerchantRefund ||
                                    !isCustomerPurchaseOrRefund ||
                                    isCustomerPurchase) && (
                                    <div className={styles.row}>
                                        <p>
                                            Total fees (included)
                                            <button
                                                type="button"
                                                className={styles.fee_info_btn}
                                                onClick={() => setShowFees(true)}>
                                                <Icon name="info" width={16} height={16} />
                                            </button>
                                        </p>
                                        <p className={styles.row_value}>{formattedFeeAmount}</p>
                                    </div>
                                )}
                            {isMerchantRefund && (
                                <div className={styles.row}>
                                    <p>Total charged</p>
                                    <p className={styles.row_value}>
                                        <b>
                                            {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                            {prettifyAmount(
                                                (transaction.amount + transaction.fee).toFixed(2)
                                            )}{' '}
                                            {transaction?.currency || 'CAD'}
                                        </b>
                                    </p>
                                </div>
                            )}
                            {fullInfoStatuses.includes(transaction?.status) &&
                                getRecipientString && (
                                    <div className={styles.row}>
                                        <p>
                                            {transaction.status !== ETransactionStatus.APPROVED
                                                ? (getRecipientString?.labelNeutral ??
                                                      `Amount ${getRecipientString?.neutral}`)
                                                : (getRecipientString?.labelPast ??
                                                      `Amount ${getRecipientString?.past}`)}
                                        </p>
                                        <p className={styles.row_value}>
                                            <b>
                                                {' '}
                                                {currencies[transaction?.currency as 'CAD']
                                                    ?.symbol || '$'}
                                                {prettifyAmount(
                                                    getRecipientString?.amount?.toFixed(2) ||
                                                        '0.00'
                                                )}{' '}
                                                {transaction?.currency || 'CAD'}
                                            </b>
                                        </p>
                                    </div>
                                )}
                        </>
                    )}
                    {/*//@ts-ignore*/}
                    {!isNotMessage && transaction?.details?.message && (
                        <div className={styles.row}>
                            <p>
                                {transaction.method === ETransactionMethod.GIFT_CARD
                                    ? 'Reference'
                                    : 'Message'}
                            </p>
                            <p className={styles.row_value}>
                                {/*//@ts-ignore*/}
                                {transaction?.details?.message || '-'}
                            </p>
                        </div>
                    )}
                    {fullInfoStatuses.includes(transaction?.status) &&
                        transaction?.points !== 0 && (
                            <div className={styles.row}>
                                <p>
                                    Points you{' '}
                                    {transaction?.status === ETransactionStatus.APPROVED
                                        ? 'earned'
                                        : 'earn'}{' '}
                                    {/* Figma: purchase/refund detail rows show no (i) tooltip on Points */}
                                    {!isPaymentRefundView && (
                                        <Info
                                            variant="top"
                                            content={`Points you ${
                                                transaction?.status === ETransactionStatus.APPROVED
                                                    ? 'earned'
                                                    : 'will earn'
                                            } for transaction with your current level`}
                                        />
                                    )}{' '}
                                </p>
                                <p className={styles.row_value}>
                                    {transaction?.points?.toFixed(2)}
                                </p>
                            </div>
                        )}
                    {transaction.action === ETransactionAction.REFUND &&
                        (transaction as any).details?.refund_reason && (
                        <div className={styles.row}>
                            <p>Refund reason</p>
                            <p className={styles.row_value}>
                                {(transaction as any).details.refund_reason}
                            </p>
                        </div>
                    )}
                    {transaction.action === ETransactionAction.REFUND &&
                        transaction.details?.order_id && (
                        <div className={styles.row}>
                            <p>Original order ID</p>
                            <p className={styles.row_value}>{orderReferenceCode ?? '—'}</p>
                        </div>
                    )}
                    {transaction?.details?.etransfer_reference_number &&
                        transaction.method === ETransactionMethod.E_TRANSFER &&
                        !(transaction.action === ETransactionAction.CARD_DEPOSIT) && (
                            <div className={styles.row}>
                                <p>{t('reference')}</p>
                                <p className={styles.row_value}>
                                    {transaction?.details?.etransfer_reference_number}
                                </p>
                            </div>
                        )}
                    {paymentSubtitleKey !== undefined && transaction.details.order_id && (
                        <div className={styles.row}>
                            <p>{t('order_id')}</p>
                            <p className={styles.row_value}>{orderReferenceCode ?? '—'}</p>
                        </div>
                    )}
                    {paymentSubtitleKey !== undefined && transaction.details.order_description && (
                        <div className={styles.row}>
                            <p>{t('description')}</p>
                            <p className={styles.row_value}>{transaction.details.order_description}</p>
                        </div>
                    )}
                    {transaction.method === ETransactionMethod.GIFT_CARD &&
                        (transaction.details.gift_card_transaction_fee?.length || 0) > 1 && (
                            <div className={styles.dropdown_row}>
                                <p onClick={() => setIsListOpen(!isListOpen)}>
                                    Gift cards included <Icon name="nav_down" />
                                </p>
                                <div
                                    className={`${styles.list} ${isListOpen ? styles.isOpen : ''}`}>
                                    {transaction.details.gift_card_transaction_fee?.map(
                                        (item, i) => (
                                            <Typography variant="body4" key={i}>
                                                {item.gift_card_serial_number.toString()} - $
                                                {prettifyAmount(item.amount.toFixed(2))} CAD
                                            </Typography>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                    <div className={styles.row}>
                        <p>
                            {transaction.action === ETransactionAction.REQUEST
                                ? 'Request ID'
                                : 'Transaction ID'}
                        </p>
                        <p className={styles.row_value}>{transaction.reference_code ?? '—'}</p>
                    </div>
                </div>
                )}
            </div>
            <div className={styles.actions_wrapper}>
                {/* Figma layout:
                      Default:       [Report problem]
                      +Refund:       [Report problem] [Refund]
                      Cancel req:    [Report problem] [Cancel request]
                      Credit req:    [Report problem] [Decline] [Approve]  (3 buttons → actions_three)
                    Send reminder appears below the main row for debit requests. */}
                <div
                    className={`${styles.actions}${
                        isCreditRequest &&
                        isInternalTransactionRequest &&
                        transaction?.status === ETransactionStatus.PENDING
                            ? ` ${styles.actions_three}`
                            : ''
                    }`}>
                    <ReportProblemButton onClick={() => setShowReport(true)} />
                    {canBeRefunded && (
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            onClick={() => setShowRefund(true)}>
                            {submitLocale('refund')}
                        </Button>
                    )}
                    {isDebitRequest &&
                        isInternalTransactionRequest &&
                        transaction?.status === ETransactionStatus.PENDING && (
                            <Button
                                variant="red"
                                size="medium"
                                onClick={handleCancelTransaction}
                                isLoading={isCancelLoading}>
                                {submitLocale('cancel_request')}
                            </Button>
                        )}
                    {isCreditRequest &&
                        isInternalTransactionRequest &&
                        transaction?.status === ETransactionStatus.PENDING && (
                            <div className={styles.primary_btn_group}>
                                <Button
                                    variant="red"
                                    size="medium"
                                    onClick={handleDeclineTransaction}
                                    isLoading={isDeclineLoading}>
                                    {submitLocale('decline')}
                                </Button>
                                <Button
                                    variant="green"
                                    size="medium"
                                    onClick={handleApproveTransaction}
                                    isLoading={isApproveLoading}>
                                    {submitLocale('approve')}
                                </Button>
                            </div>
                        )}
                </div>
                {isDebitRequest &&
                    isInternalTransactionRequest &&
                    transaction?.status === ETransactionStatus.PENDING && (
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            disabled={reminderTimer !== 0}
                            onClick={handleSendReminder}
                            isLoading={isResendLoading}>
                            {submitLocale('send_reminder')}{' '}
                            {reminderTimer !== 0 && `(${reminderTimer}s)`}
                        </Button>
                    )}
            </div>
            </>
            )}
        </div>
    );
};

export default ViewTransactionModal;
